export const oneOnOneEnTranslations = {
  // Wizard Progress
  goalsReviewProgress: 'Goals Review Progress',
  goalOfTotal: (current: number, total: number) =>
    `Goal ${current} of ${total}`,
  noGoalsToReview: 'No goals to review',
  noGoalsAssigned:
    'There are no goals assigned to you for this one-on-one yet.',
  meetingProgress: 'Meeting Progress',
  goalLabel: 'Goal',
  of: 'of',

  // Navigation Buttons
  previous: 'Previous',
  next: 'Next',
  previousGoal: 'Previous Goal',
  nextGoal: 'Next Goal',
  skip: 'Skip',
  completeReview: 'Complete Review',
  backToGoalsReview: 'Back to Goals Review',
  backToOneOnOnes: 'Back to One-on-Ones',

  // Goal Detail Card
  keyResults: 'Key Results',
  timeline: 'Timeline',
  start: 'Start',
  due: 'Due',
  noDatesSet: 'No dates set for this goal',
  noKeyResultsDefined: 'No key results defined for this goal',
  relatedTasks: 'Related Tasks',
  noTasksAssigned: 'No tasks assigned to you for this goal',
  completedOn: ({ date }: { date: string }) => `Completed on ${date}`,
  tasksTable: {
    status: 'Status',
    task: 'Task',
    assignees: 'Assignees',
    startDate: 'Start Date',
    dueDate: 'Due Date',
  },

  // Interactions List
  noInteractions: 'No interactions have been logged for this goal yet.',
  previousInteractions: 'Previous Interactions',
  loggedOn: 'Logged on',
  interactionDetailsAvailable: 'Interaction details available',
  viewDetails: 'View details',
  participant: 'participant',
  participants: 'participants',
  unknown: 'Unknown',
  new: 'New',

  // Interaction Types
  iSeeYou: 'I See You',
  iNeedYou: 'I Need You',
  iLearn: 'I Learn',

  // Interaction Creation Card
  createInteractionForGoal: 'Create Interaction for This Goal',
  createInteractionDescription:
    'Add recognition, feedback, or document learning related to this goal.',
  iSeeYouDescription: 'Recognize great work and celebrate achievements',
  iNeedYouDescription: 'Share constructive feedback to help grow',
  iLearnDescription: 'Document learnings and insights',

  // Interaction Wizard Drawer
  oneOnOneInProgress: 'One-on-One in Progress',
  creatingForGoal: 'Creating for goal',

  // Wizard Stepper
  goalsReview: 'Goals Review',
  summary: 'Summary',

  // Summary Stage
  oneOnOneSummary: 'One-on-One Summary',
  completedOneOnOne: 'Completed One-on-One',
  summaryDescription:
    'Review the summary of your one-on-one session below. You can edit any section or submit to complete the session.',
  totalGoals: 'Total Goals',
  goalsReviewed: 'Goals Reviewed',
  status: 'Status',
  complete: 'Complete',
  reviewedGoals: 'Reviewed Goals',
  editGoalsReview: 'Edit Goals Review',
  readyToSubmit: 'Ready to Submit?',
  submitDescription:
    'Once submitted, this one-on-one session will be marked as complete.',
  submitOneOnOne: 'Submit One-on-One',
  submitting: 'Submitting...',
  sentToManager: 'Sent to Manager',
  waitingForManagerToSubmit: 'Waiting for manager to finish the one-on-one',
  failedToSaveNotes: 'Failed to save notes. Please try again.',
  onlyEmployeeCanCreate: 'Only employees can create one-on-one meetings',
  oneOnOneCompleted: 'One-on-One Completed',
  oneOnOneCompletedDescription:
    'This one-on-one session has been completed and finished',

  // Toast Notifications
  goalsReviewSaved: 'Goals Review Saved',
  successfullySavedReview: (count: number) =>
    `Successfully saved review for ${count} goal${count !== 1 ? 's' : ''}`,
  failedToSave: 'Failed to Save',
  couldNotSaveReview: 'Could not save goals review. Please try again.',
  failedToUpdateStatus: 'Failed to Update Status',
  couldNotUpdateGoalStatus: 'Could not update goal status. Please try again.',
  failedToUpdateTaskStatus: 'Failed to Update Task Status',
  couldNotUpdateTaskStatus: 'Could not update task status. Please try again.',
  taskStatusUpdated: 'Task Status Updated',
  taskStatusUpdatedSuccessfully: 'Task status has been updated successfully.',
  viewTask: 'View task',
  relatedSignalsTooltip: (count: number) =>
    count === 1 ? '1 related signal' : `${count} related signals`,
  deleteTask: 'Delete Task',
  deleteTaskTitle: 'Delete Task',
  deleteTaskDescription: (taskTitle: string) =>
    `Are you sure you want to delete "${taskTitle}"? This action cannot be undone.`,
  deleteTaskConfirm: 'Delete',
  deleteTaskCancel: 'Cancel',
  deleteTaskDeleting: 'Deleting...',
  taskDeleted: 'Task Deleted',
  taskDeletedSuccessfully: 'Task has been deleted successfully',
  failedToDeleteTask: 'Failed to Delete Task',
  couldNotDeleteTask: 'Could not delete task. Please try again.',

  // Landing Page
  oneOnOne: 'One-on-One',
  prepareDescription:
    'Prepare for your one-on-one sessions with our comprehensive wizard. Review goals, reflect on progress, and document your session outcomes.',
  wizardDescription:
    'The wizard guides you through reviewing assigned goals and their related interactions, then helps you create a summary of your one-on-one session.',
  startOneOnOneWizard: 'Start One-on-One Wizard',

  // Route Page Headers
  oneOnOneWizard: 'One-on-One Wizard',
  prepareForUpcoming: 'Prepare for your upcoming one-on-one',
  viewCompletedOneOnOne: 'View completed one-on-one',
  reviewGoalsDescription:
    'Review your goals, reflect on recent progress, and complete your one-on-one session summary.',
  reviewCompletedDescription:
    'View the summary of your completed one-on-one session, including all goals, interactions, and meeting notes.',

  // Common
  processing: 'Processing...',
  goal: 'Goal',
  id: 'ID',

  // Summary Stage - New
  openBlockages: 'Open Blockages',
  totalInteractions: 'Total Interactions',
  goalsOnTrack: 'Goals on Track',
  goalsAndInteractions: 'Goals & Interactions',
  reviewYourGoalsAndInteractions:
    'Review all goals and their related interactions',
  blockagesCount: (count: number) =>
    count === 1 ? `${count} Open Blockage` : `${count} Open Blockages`,
  interactions: 'Interactions',
  notSet: 'Not set',
  onTrack: 'On Track',
  offTrack: 'Off Track',
  interactionCount: (count: number) =>
    count === 1 ? `${count} Interaction` : `${count} Interactions`,

  // Meeting Notes
  meetingNotes: 'Meeting Notes',
  meetingNotesDescription:
    'Add notes and key takeaways from your one-on-one session',
  meetingNotesFromSession: 'Notes from your completed one-on-one session',
  noNotesAdded: 'No notes were added during this session',
  addNotePlaceholder: 'Type your note here...',
  pressEnterToAdd: 'Press Cmd/Ctrl + Enter to add',
  addNote: 'Add Note',
  noteCount: (count: number) =>
    count === 1 ? `${count} note added` : `${count} notes added`,
  goalsAndNotes: 'Goals & Notes',
  reviewGoalsAndAddNotes: 'Review your goals and add meeting notes',

  // Landing Page - One-on-Ones List
  myOneOnOnes: 'My One-on-Ones',
  teamOneOnOnes: 'Team One-on-Ones',
  oneOnOnes: 'One-on-Ones',
  total: 'Total',
  loading: 'Loading...',
  oneOnOneWith: 'One-on-One with',
  notes: 'notes',
  noOneOnOnesFound: 'No one-on-ones found',
  scheduled: 'Scheduled',
  inProgress: 'In Progress',
  readyForSubmission: 'Ready for Meeting',
  completed: 'Completed',
  locked: 'Finished',
  stagesCompleted: 'stages completed',
  startNewOneOnOne: 'Start New One-on-One',
  creating: 'Creating...',
  oneOnOneCreated: 'One-on-One Created',
  oneOnOneCreatedDescription:
    'Your one-on-one session has been created successfully',
  oneOnOneCreatedWithEmployee:
    'One-on-one session created successfully with employee',
  failedToCreate: 'Failed to Create',
  couldNotCreateOneOnOne: 'Could not create one-on-one. Please try again.',
  noManagerAssigned: 'No Direct Manager',
  noManagerDescription:
    'A direct manager is needed to start a one-on-one session.',
  createOneOnOne: 'Create One-on-One',
  createMeeting: 'Create Meeting',
  createOneOnOneWithEmployee: 'Create One-on-One with Employee',
  selectEmployeeForOneOnOne:
    'Select an employee to schedule a one-on-one session',
  cancel: 'Cancel',
  create: 'Create',

  // Sidebar
  your1on1Meetings: 'Your 1:1 Meetings',
  myManager: 'My Manager',
  teamSize: 'Team Size',
  directReports: 'Direct Reports',
  yourTeam: 'Your Team',
  sortBy: 'Sort by',
  needsAttention: 'Needs Attention',
  hasntHadMeetingIn: (name: string, days: number) =>
    `${name} hasn't had a meeting in ${days} days`,
  create1on1WithManager: 'Create 1:1 with Manager',

  // Meeting History
  meetingHistory: 'Meeting History',
  newMeeting: 'New Meeting',
  totalMeetingSingular: 'total meeting',
  totalMeetingsPlural: 'total meetings',
  totalMeetings: (count: number) =>
    count === 1 ? `${count} total meeting` : `${count} total meetings`,
  lastMeeting: 'Last meeting',
  daysAgo: (days: number) =>
    days === 1 ? `${days} day ago` : `${days} days ago`,
  duration: 'Duration',
  minutes: 'min',
  min: 'min',
  discussed: 'Discussed',
  selectEmployeeToView: 'Select an employee to view their meeting history',
  never: 'Never',
  employee: 'Employee',
  lastMeetingText: '3 days ago',
  employeeNeedsAttention: (name: string) =>
    `${name} hasn't had a meeting in 28 days`,
  seniorDeveloperEngineering: 'Senior Developer • Engineering',
  managerWithEmployee: (firstName: string, lastName: string) =>
    `You (Manager) with ${firstName} ${lastName}`,
  performanceReview: 'Performance Review',
  stages: 'stages',

  // Coworker 1:1
  create1on1WithCoworker: 'Create 1:1 with Coworker',
  selectCoworker: 'Select a Coworker',
  selectCoworkerDescription:
    'Choose a colleague to review goals together in a one-on-one session',
  noCoworkersAvailable: 'No Coworkers Available',
  noCoworkersAvailableDescription:
    'There are no eligible coworkers available to create a one-on-one with at this time.',
  creating1on1WithCoworker: 'Creating 1:1...',

  // Professional Meetings
  professionalMeetings: '1:1 Meetings with My Managers',
  professionalMeetingsFacilitator: '1:1 Meetings with Employees',
  createProfessionalMeeting: 'Create 1:1 Meeting with Another Manager',

  // Meeting Timeline
  created: 'Created',
  finished: 'Finished',
  at: 'at',

  // Meeting Titles
  oneOnOneMeetingTitle: 'One-on-One Meeting',
  professionalMeetingTitle: 'Professional Meeting',

  // Replace Open Meeting Dialog
  replaceOpenMeeting: 'Open Meeting Exists',
  replaceOpenMeetingDescription: (employeeName: string) =>
    `You already have an open meeting with ${employeeName}. To create a new meeting, you'll need to replace the existing one.`,
  replaceMeeting: 'Replace & Create New',
  replacing: 'Replacing...',
  meetingReplaced: 'Meeting Replaced',
  meetingReplacedDescription:
    'The existing meeting has been replaced and a new one created',
  failedToReplace: 'Failed to Replace',
  couldNotReplaceMeeting: 'Could not replace the meeting. Please try again.',
};
